import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, Video, Activity } from 'lucide-react';
import { formatTime12h } from '../lib/utils';
import DoctorDashboard from './DoctorDashboard';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect admin roles to admin panel
  useEffect(() => {
    if (user && (user.role === 'super_admin' || user.role === 'hospital_admin')) {
      navigate('/admin/dashboard');
      return;
    }
  }, [user, navigate]);

  // If user is a doctor, show doctor-specific dashboard
  if (user?.role === 'doctor') {
    return <DoctorDashboard />;
  }
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, appointmentsRes] = await Promise.all([
          axiosInstance.get('/dashboard/stats'),
          axiosInstance.get('/appointments')
        ]);
        setStats(statsRes.data);
        setAppointments(appointmentsRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'completed': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-100';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-manrope font-bold mb-2" data-testid="dashboard-title">
            Welcome back, {user?.full_name}
          </h1>
          <p className="text-slate-600">Here's your healthcare overview</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {user?.role === 'patient' ? (
              <>
                <Card data-testid="stat-total-appointments">
                  <CardHeader className="pb-2">
                    <CardDescription>Total Appointments</CardDescription>
                    <CardTitle className="text-3xl">{stats.total_appointments || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card data-testid="stat-upcoming-appointments">
                  <CardHeader className="pb-2">
                    <CardDescription>Upcoming</CardDescription>
                    <CardTitle className="text-3xl text-primary">{stats.upcoming_appointments || 0}</CardTitle>
                  </CardHeader>
                </Card>
              </>
            ) : (
              <>
                <Card data-testid="stat-total-patients">
                  <CardHeader className="pb-2">
                    <CardDescription>Total Patients</CardDescription>
                    <CardTitle className="text-3xl">{stats.total_patients || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card data-testid="stat-total-doctors">
                  <CardHeader className="pb-2">
                    <CardDescription>Total Doctors</CardDescription>
                    <CardTitle className="text-3xl">{stats.total_doctors || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card data-testid="stat-total-appointments-admin">
                  <CardHeader className="pb-2">
                    <CardDescription>Total Appointments</CardDescription>
                    <CardTitle className="text-3xl">{stats.total_appointments || 0}</CardTitle>
                  </CardHeader>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Quick Actions */}
        {user?.role === 'patient' && (
          <Card className="mb-8" data-testid="quick-actions">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/book')} data-testid="book-appointment-button">
                <Calendar className="h-4 w-4 mr-2" />
                Book New Appointment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Appointments List */}
        <Card data-testid="appointments-list">
          <CardHeader>
            <CardTitle>Your Appointments</CardTitle>
            <CardDescription>
              {appointments.length === 0 ? 'No appointments yet' : `${appointments.length} appointment(s)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {appointments.slice(0, 5).map((apt, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`appointment-item-${index}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={getStatusColor(apt.status)}>
                        {apt.status}
                      </Badge>
                    </div>
                    <p className="font-medium text-slate-900">
                      {apt.consultation_type === 'video' && <Video className="inline h-4 w-4 mr-1 text-primary" />}
                      Appointment
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {apt.appointment_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTime12h(apt.appointment_time)}
                      </span>
                    </div>
                  </div>
                  {apt.google_meet_link && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(apt.google_meet_link, '_blank')}
                      data-testid={`join-video-${index}`}
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Join Video
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}